import { fromScaled, toScaled } from '@colplat/protocol';
import type { Ledger } from './rows.js';
import { walletLegs } from './wallets.js';

/**
 * An account's balance at the end of each day it moved, split by the wallet
 * each part of it belongs to.
 *
 * The wallet page already draws a wallet's balance over time. This is the
 * other cut of the same legs: the account is fixed and the wallets are the
 * parts, so a reader can see not only that the Safe held 900,000 forints on
 * a given evening but whose they were.
 *
 * Per currency and never combined, for the reason `walletHistory` gives: the
 * figures are not comparable, and one axis across forints and euros draws the
 * forint and a flat line.
 *
 * One point per DAY. Several entries on one date are one thing that happened
 * to the account, and the last of them is where the day left it.
 */

/** The key a leg with no wallet is filed under. A wallet id is never empty. */
export const NO_WALLET = '';

export interface AccountHistoryPoint {
  /** `YYYY-MM-DD`. */
  date: string;
  /** The account's balance in this currency at the end of the day. */
  total: string;
  /**
   * The same balance by wallet, keyed by wallet id or `NO_WALLET`. Every key
   * the series ever uses is present on every point, at `'0.0000'` where that
   * wallet holds nothing that day, so a chart can stack them in one order.
   */
  parts: Record<string, string>;
}

export interface AccountHistory {
  currencyCode: string;
  /** The keys in `parts`, in the order they first appear. */
  keys: string[];
  points: AccountHistoryPoint[];
}

export interface AccountHistories {
  /** Each account's own legs only — what an account's "own" balance is. */
  own: Map<string, AccountHistory[]>;
  /**
   * Each account and everything filed under it — what the Total column of
   * the chart of accounts shows, and so what a chart beside it must show.
   */
  subtree: Map<string, AccountHistory[]>;
}

interface Movement { date: string; key: string; amount: bigint }

/**
 * Both cuts for every account, in one pass over the legs.
 *
 * Only legs of live transactions, the same rule `accountsView` applies: a
 * device mid-sync can hold a leg whose transaction it has already seen
 * deleted, and that leg is not a balance.
 */
export function accountHistories(ledger: Ledger): AccountHistories {
  const dateOf = new Map(ledger.transactions().map((t) => [t.id, t.txDate] as const));
  const movements = new Map<string, Map<string, Movement[]>>();
  for (const leg of walletLegs(ledger)) {
    const date = dateOf.get(leg.transactionId);
    if (date === undefined) continue;
    let byCcy = movements.get(leg.accountId);
    if (!byCcy) { byCcy = new Map(); movements.set(leg.accountId, byCcy); }
    let list = byCcy.get(leg.currencyCode);
    if (!list) { list = []; byCcy.set(leg.currencyCode, list); }
    list.push({ date, key: leg.walletId ?? NO_WALLET, amount: toScaled(leg.amount) });
  }

  const accounts = ledger.accounts();
  const childrenOf = new Map<string, string[]>();
  for (const a of accounts) {
    if (!a.parentId) continue;
    const list = childrenOf.get(a.parentId);
    if (list) list.push(a.id); else childrenOf.set(a.parentId, [a.id]);
  }
  /** Depth-guarded: a parent chain is data, and a cycle would hang the page. */
  const subtreeOf = (rootId: string): string[] => {
    const out = [rootId];
    const seen = new Set(out);
    for (let i = 0; i < out.length && i < 10_000; i += 1) {
      for (const child of childrenOf.get(out[i]!) ?? []) {
        if (seen.has(child)) continue;
        seen.add(child);
        out.push(child);
      }
    }
    return out;
  };

  const own = new Map<string, AccountHistory[]>();
  const subtree = new Map<string, AccountHistory[]>();
  for (const a of accounts) {
    own.set(a.id, seriesOf([movements.get(a.id)]));
    subtree.set(a.id, seriesOf(subtreeOf(a.id).map((id) => movements.get(id))));
  }
  return { own, subtree };
}

function seriesOf(sources: ReadonlyArray<Map<string, Movement[]> | undefined>): AccountHistory[] {
  const byCcy = new Map<string, Movement[]>();
  for (const source of sources) {
    for (const [ccy, list] of source ?? []) {
      const all = byCcy.get(ccy);
      if (all) all.push(...list); else byCcy.set(ccy, [...list]);
    }
  }
  return [...byCcy.entries()]
    .map(([currencyCode, list]) => series(currencyCode, list))
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
}

function series(currencyCode: string, list: Movement[]): AccountHistory {
  // Stable on date alone: the order within a day does not matter, because
  // only the day's end is reported.
  const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
  const keys: string[] = [];
  for (const m of sorted) if (!keys.includes(m.key)) keys.push(m.key);

  const running = new Map<string, bigint>(keys.map((k) => [k, 0n]));
  const points: AccountHistoryPoint[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const m = sorted[i]!;
    running.set(m.key, running.get(m.key)! + m.amount);
    // Only at the end of a day: the next movement is on a later date, or
    // there is none.
    if (i + 1 < sorted.length && sorted[i + 1]!.date === m.date) continue;
    let total = 0n;
    const parts: Record<string, string> = {};
    for (const k of keys) {
      const v = running.get(k)!;
      total += v;
      parts[k] = fromScaled(v);
    }
    points.push({ date: m.date, total: fromScaled(total), parts });
  }
  return { currencyCode, keys, points };
}

/**
 * Each wallet's balance at the end of every day it moved, per currency,
 * split by the ACCOUNT holding each part — the accounts page's cut turned
 * round. The wallets page asks "where is this wallet's money", and the
 * answer is a list of accounts.
 *
 * Money legs only (ASSET and LIABILITY), the same as the wallet's own
 * balance, and rolled up: a wallet includes everything filed inside it, as
 * its balance on the wallets page does. Money in no wallet is under
 * `NO_WALLET`, so the book is the top-level wallets and that together.
 */
export function walletAccountHistories(ledger: Ledger): Map<string, AccountHistory[]> {
  const dateOf = new Map(ledger.transactions().map((t) => [t.id, t.txDate] as const));
  const money = new Set(ledger.accounts().filter((a) => a.type === 'ASSET' || a.type === 'LIABILITY').map((a) => a.id));
  const movements = new Map<string, Map<string, Movement[]>>();
  for (const leg of walletLegs(ledger)) {
    const date = dateOf.get(leg.transactionId);
    if (date === undefined || !money.has(leg.accountId)) continue;
    const w = leg.walletId ?? NO_WALLET;
    let byCcy = movements.get(w);
    if (!byCcy) { byCcy = new Map(); movements.set(w, byCcy); }
    let list = byCcy.get(leg.currencyCode);
    if (!list) { list = []; byCcy.set(leg.currencyCode, list); }
    list.push({ date, key: leg.accountId, amount: toScaled(leg.amount) });
  }
  const wallets = ledger.wallets();
  const childrenOf = new Map<string, string[]>();
  for (const w of wallets) {
    if (!w.parentId) continue;
    const list = childrenOf.get(w.parentId);
    if (list) list.push(w.id); else childrenOf.set(w.parentId, [w.id]);
  }
  const out = new Map<string, AccountHistory[]>();
  for (const id of [...wallets.map((w) => w.id), NO_WALLET]) {
    const ids = [id];
    const seen = new Set(ids);
    for (let i = 0; i < ids.length && i < 10_000; i += 1) {
      for (const child of childrenOf.get(ids[i]!) ?? []) {
        if (!seen.has(child)) { seen.add(child); ids.push(child); }
      }
    }
    out.set(id, seriesOf(ids.map((x) => movements.get(x))));
  }
  return out;
}
